'use client';

import { cn } from '@repo/ui/lib/utils';

import type { InvestorFeesResponse } from '../types/api';
import {
  excludedFeeEventLines,
  feeNotes,
  highWaterMarkLine,
  pendingFeesFigure,
  positionPendingLine
} from '../utils/fees-and-capital';
import { formatBasisPoints } from '@/lib/format/percent/basis-points';
import { formatPercent } from '@/lib/format/percent/percent';
import { formatUsdFigure } from '@/lib/format/usd/usd-figure';
import { netOfPendingLine } from '@/lib/net-of-pending';
import {
  ErrorBlock,
  LIVE_BADGE,
  LoadingBlock,
  SectionShell
} from './fees-and-capital-section';

/** A fee drag in basis points, as a percentage with two places. */
const FEE_DRAG = { from: 'bps', digits: 2, sign: 'minus' } as const;
/** A return in the walk, and the net return's twin, in the same places. */
const WALK_RETURN = { signed: true, fractionDigits: 2 } as const;

/** One line of the gross to net walk. `detail` is a note, never a step. */
interface WalkRow {
  label: string;
  value: string;
  strong?: boolean;
  detail?: string | null;
}

interface FeesAndCostsProps {
  fees?: InvestorFeesResponse;
  isLoading?: boolean;
  isError?: boolean;
  periodLabel: string;
}

/**
 * Fees & costs (spec 1.4), realized-charged basis: what the fees actually
 * cost this investor. Window, month to date, and year to date totals; the
 * gross to net walk on served per-type drag (never derived client side);
 * the fee mix in dollars; and per position accruals with each vault's
 * distance to its high water mark when the backend serves it.
 */
export function FeesAndCosts({
  fees,
  isLoading = false,
  isError = false,
  periodLabel
}: FeesAndCostsProps) {
  const drag = fees?.dragBps;
  const walk: WalkRow[] | null =
    fees != null && fees.grossReturnBps != null && fees.netReturnBps != null
      ? [
          {
            label: 'Gross return',
            value: formatBasisPoints(fees.grossReturnBps, WALK_RETURN),
            strong: true
          },
          ...(drag?.managementBps != null
            ? [
                {
                  label: 'Management fees',
                  value: `−${formatPercent(drag.managementBps, FEE_DRAG)}`
                }
              ]
            : []),
          ...(drag?.performanceBps != null
            ? [
                {
                  label: 'Performance fees',
                  value: `−${formatPercent(drag.performanceBps, FEE_DRAG)}`
                }
              ]
            : []),
          ...(drag?.protocolBps != null
            ? [
                {
                  label: 'Protocol fees',
                  value: `−${formatPercent(drag.protocolBps, FEE_DRAG)}`
                }
              ]
            : []),
          ...(drag?.curatorBps != null
            ? [
                {
                  label: 'Curator fees',
                  value: `−${formatPercent(drag.curatorBps, FEE_DRAG)}`
                }
              ]
            : []),
          {
            label: 'Net return',
            value: formatBasisPoints(fees.netReturnBps, WALK_RETURN),
            strong: true,
            // API 1.159.0: the net return with the fees still building up
            // taken off too. A note under the label, never a step: the
            // walk reconciles charged fees only.
            detail: netOfPendingLine(
              fees.netReturnNetOfPendingBps,
              fees.netReturnNetOfPendingReason,
              (bps) => formatBasisPoints(bps, WALK_RETURN)
            )
          }
        ]
      : null;

  // Every part of `totalUsd`, so the rows add up to the total above them.
  // Curator fees are part of that total whenever they are served; leaving
  // the row out made the list fall short of it.
  const mix = fees
    ? [
        { label: 'Management', value: fees.accrued.managementUsd },
        { label: 'Performance', value: fees.accrued.performanceUsd },
        { label: 'Protocol', value: fees.accrued.protocolUsd },
        ...(fees.accrued.curatorUsd != null
          ? [{ label: 'Curator', value: fees.accrued.curatorUsd }]
          : []),
        { label: 'Your deposit fees', value: fees.accrued.entryUsd },
        { label: 'Your withdrawal fees', value: fees.accrued.exitUsd }
      ]
    : [];
  // The served warnings in the reader's words; the served text never shows.
  const notes = fees ? feeNotes(fees) : [];
  // The fee charges left out of the totals, one line each (API 1.157).
  const excluded = fees ? excludedFeeEventLines(fees) : [];
  // Fees building up since each vault's last charge (API 1.157). Shown
  // beside the charged figures and never taken off them: the share price,
  // and so every value and return here, does not count them yet.
  const pending = fees ? pendingFeesFigure(fees) : null;
  // The subtitle names the fees building up only when the backend served
  // them (API 1.157); before that, everything here was charged.
  const hasPendingFees =
    fees != null &&
    (fees.pendingFees != null ||
      fees.positions.some((position) => position.pendingFees != null));
  // The waiting line is only true while no held vault serves the distance.
  const highWaterMarkPending =
    fees != null &&
    fees.positions.length > 0 &&
    fees.positions.every((position) => position.distanceToHwmBps == null);

  return (
    <SectionShell
      titleId="fees-costs-title"
      title="Fees & costs"
      note={
        hasPendingFees
          ? 'Totals count only fees already charged. Fees still building up are shown on their own.'
          : 'Only fees charged on chain are counted here.'
      }
      badge={fees ? LIVE_BADGE : undefined}
    >
      {isLoading ? (
        <LoadingBlock />
      ) : isError || !fees ? (
        <ErrorBlock />
      ) : (
        <>
          <dl className="mt-5 grid grid-cols-3 gap-3">
            {[
              { label: periodLabel, value: fees.accrued.totalUsd },
              { label: 'Month to date', value: fees.accruedMtd.totalUsd },
              { label: 'Year to date', value: fees.accruedYtd.totalUsd }
            ].map((stat) => (
              <div
                key={stat.label}
                className="bg-rowa-surface-inset rounded-rowa-md px-3 py-2.5"
              >
                <dt className="text-rowa-label-sm text-rowa-text-tertiary tracking-[0.08em] uppercase">
                  {stat.label}
                </dt>
                <dd className="text-rowa-data-medium text-rowa-text-primary mt-1 tabular-nums">
                  {formatUsdFigure(stat.value, { maximumFractionDigits: 2 })}
                </dd>
              </div>
            ))}
          </dl>

          {walk ? (
            <div className="mt-6">
              <h3 className="text-rowa-label-md text-rowa-text-primary font-medium">
                From gross to net
              </h3>
              <dl className="divide-rowa-border-subtle mt-2 divide-y">
                {walk.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-baseline justify-between gap-3 py-2"
                  >
                    <dt
                      className={cn(
                        'text-rowa-body-sm',
                        row.strong
                          ? 'text-rowa-text-primary font-medium'
                          : 'text-rowa-text-secondary'
                      )}
                    >
                      {row.label}
                      {row.detail ? (
                        <span className="text-rowa-label-sm text-rowa-text-tertiary block font-normal">
                          {row.detail}
                        </span>
                      ) : null}
                    </dt>
                    <dd
                      className={cn(
                        'text-rowa-body-sm text-right font-mono tabular-nums',
                        row.strong
                          ? 'text-rowa-text-primary font-medium'
                          : 'text-rowa-text-secondary'
                      )}
                    >
                      {row.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : (
            <p className="text-rowa-body-sm text-rowa-text-secondary mt-6">
              The gross to net walk appears once this window has a return.
            </p>
          )}

          <div className="mt-6">
            <h3 className="text-rowa-label-md text-rowa-text-primary font-medium">
              What you paid, by type
            </h3>
            <dl className="mt-2 space-y-1.5">
              {mix.map((row) => (
                <div
                  key={row.label}
                  className="flex items-baseline justify-between gap-3"
                >
                  <dt className="text-rowa-body-sm text-rowa-text-secondary">
                    {row.label}
                  </dt>
                  <dd className="text-rowa-body-sm text-rowa-text-primary text-right font-mono tabular-nums">
                    {formatUsdFigure(row.value, { maximumFractionDigits: 2 })}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="text-rowa-label-sm text-rowa-text-tertiary mt-2">
              Deposit and withdrawal fees are one time charges on your own
              flows. They count in the totals but sit outside the expense ratio.
            </p>
            {/* True on chain: a vault mints its management and performance
                fees when it charges them, which happens on every deposit,
                withdrawal and redemption and whenever anyone triggers it.
                Fees building up since the last charge are not yet minted. */}
            <p className="text-rowa-label-sm text-rowa-text-tertiary mt-1">
              Vaults charge management and performance fees from time to time,
              for example when money moves in or out of the vault. Fees that
              build up between charges join these totals once charged.
            </p>
          </div>

          {pending ? (
            <div className="mt-6">
              <h3 className="text-rowa-label-md text-rowa-text-primary font-medium">
                Building up, not yet charged
              </h3>
              <dl className="mt-2">
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-rowa-body-sm text-rowa-text-secondary">
                    Your share
                    {pending.detail ? (
                      <span className="text-rowa-label-sm text-rowa-text-tertiary block">
                        {pending.detail}
                      </span>
                    ) : null}
                  </dt>
                  <dd className="text-rowa-body-sm text-rowa-text-primary shrink-0 text-right font-mono whitespace-nowrap tabular-nums">
                    {pending.value}
                  </dd>
                </div>
              </dl>
            </div>
          ) : null}

          {fees.positions.length > 0 ? (
            <div className="mt-6">
              <h3 className="text-rowa-label-md text-rowa-text-primary font-medium">
                By position
              </h3>
              <dl className="mt-2 space-y-1.5">
                {fees.positions.map((position) => {
                  const pendingLine = positionPendingLine(
                    position.pendingFees,
                    hasPendingFees
                  );
                  return (
                    <div
                      key={position.vaultAddress}
                      className="flex items-baseline justify-between gap-3"
                    >
                      <dt className="text-rowa-body-sm text-rowa-text-secondary">
                        {position.name}
                        {position.distanceToHwmBps != null ? (
                          <span className="text-rowa-label-sm text-rowa-text-tertiary block">
                            {highWaterMarkLine(position.distanceToHwmBps)}
                          </span>
                        ) : null}
                        {pendingLine ? (
                          <span className="text-rowa-label-sm text-rowa-text-tertiary block">
                            {pendingLine}
                          </span>
                        ) : null}
                      </dt>
                      <dd className="text-rowa-body-sm text-rowa-text-primary text-right font-mono tabular-nums">
                        {formatUsdFigure(position.accruedUsd, {
                          maximumFractionDigits: 2
                        })}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          ) : null}

          {highWaterMarkPending || notes.length > 0 || excluded.length > 0 ? (
            <div className="border-rowa-border-default text-rowa-label-sm text-rowa-text-tertiary mt-auto space-y-1 border-t pt-3 [&>*:first-child]:mt-4">
              {highWaterMarkPending ? (
                <p>
                  Distance to each vault&apos;s high water mark: waiting on
                  data.
                </p>
              ) : null}
              {/* Plain sentences, deduplicated, so each is its own key. */}
              {notes.map((note) => (
                <p key={note}>{note}</p>
              ))}
              {excluded.length > 0 ? (
                <>
                  <p>We left these fee charges out of the totals:</p>
                  <ul className="list-disc space-y-1 pl-4">
                    {excluded.map((line) => (
                      <li key={line.key}>{line.text}</li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </SectionShell>
  );
}
